import { Tableau } from '~commonInfra/utils/TableauLog';
import WebHttpClient from '~src/httpClient/WebHttpClient';

import { IPlanContentRow, loadPlanContentsPage } from '../details/planContentsService';
import { GetExecutionStatusRequest } from '../serverApi/requests/GetExecutionStatusRequest';
import { TriggerExtractionRequest } from '../serverApi/requests/TriggerExtractionRequest';
import { IExecution, ISelectedItem } from '../serverApi/types/IExecution';
import { ISelectionQuery } from '../serverApi/types/ISelectionQuery';
import { relayWireContentType } from '../utils/relaySelection';
import { hasErrors } from '../utils/resultErrors';
import { isWireTypeSupported } from '../utils/supportedContentTypes';

export const STATUS_POLL_INTERVAL_MS = 3000;
/** Consecutive failed status polls tolerated before the run is reported as lost. */
export const MAX_STATUS_POLL_FAILURES = 3;

export interface IRunPlanContents {
  readonly rows: ReadonlyArray<IPlanContentRow>;
  /** True when rows came from the plan's selection queries (no previewExtraction) — no subset runs. */
  readonly isFallback: boolean;
  /** How many items the plan resolves to, as the first page reports it. */
  readonly totalCount: number;
  /** True when loading stopped with pages left, so `rows` isn't the whole plan — no subset runs. */
  readonly isTruncated: boolean;
}

/**
 * Most preview pages the Run dialog reads (PREVIEW_PAGE_SIZE items each), so a very large plan can't keep
 * the dialog loading indefinitely. A truncated list can only run the whole plan.
 */
export const MAX_RUN_PREVIEW_PAGES = 20;

/**
 * Loads the pages of the plan's extraction preview, for the Run dialog's item list, without owner / location /
 * modified. Stops at MAX_RUN_PREVIEW_PAGES, or when the server repeats a page token it already sent.
 */
export function loadAllPlanContents(planLuid: string, selectionQueries: ReadonlyArray<ISelectionQuery>): Promise<IRunPlanContents> {
  const rows: IPlanContentRow[] = [];
  const seenTokens: { [token: string]: boolean } = {};
  let isFallback = false;
  let totalCount = 0;
  const loadFrom = function(pageToken: string, pageCount: number): Promise<IRunPlanContents> {
    return loadPlanContentsPage(planLuid, selectionQueries, pageToken, { withDetails: false }).then(function(page) {
      rows.push(...page.rows);
      // Every page of a plan comes from the same source, so the first page decides fallback mode.
      if (pageCount === 1) {
        isFallback = page.isFallback;
        totalCount = page.totalCount;
      }
      const next = page.nextPageToken;
      if (next && !seenTokens[next] && pageCount < MAX_RUN_PREVIEW_PAGES) {
        seenTokens[next] = true;
        return loadFrom(next, pageCount + 1);
      }
      if (next) {
        Tableau.Logger().info('Content Relay: stopped loading the run preview after ' + pageCount + ' pages');
      }
      return { rows: rows, isFallback: isFallback, totalCount: totalCount, isTruncated: !!next };
    });
  };
  return loadFrom(undefined, 1);
}

// The run each plan's Run dialog last started, by plan LUID, until the dialog sees it finish. Reopening the
// dialog, or closing it while the run is still being queued, shows that run rather than starting another.
const activeRuns: { [planLuid: string]: Promise<IExecution> } = {};

export function setActiveRun(planLuid: string, run: Promise<IExecution>): void {
  activeRuns[planLuid] = run;
  run.catch(function() {
    if (activeRuns[planLuid] === run) {
      clearActiveRun(planLuid);
    }
  });
}

export function getActiveRun(planLuid: string): Promise<IExecution> {
  return activeRuns[planLuid];
}

export function clearActiveRun(planLuid: string): void {
  delete activeRuns[planLuid];
}

/**
 * A row the user may pick for a run: it resolves, is downloadable, carries a LUID to send, and its type is
 * one the service currently extracts.
 */
export function isRunnable(row: IPlanContentRow): boolean {
  return !row.unavailable && row.downloadable && !!row.luid && isWireTypeSupported(relayWireContentType(row.contentType));
}

export function isTerminalState(state: string): boolean {
  return state === 'SUCCEEDED' || state === 'FAILED' || state === 'CANCELLED';
}

/**
 * Queues an extraction of the plan. `selected` lists the rows to extract; pass undefined to extract the
 * whole plan (the server treats an empty selection as everything).
 */
export function triggerExtraction(planLuid: string, selected?: ReadonlyArray<IPlanContentRow>): Promise<IExecution> {
  const selectedItems: ISelectedItem[] = (selected || []).map(function(row) {
    return { contentType: relayWireContentType(row.contentType), luid: row.luid };
  });
  return WebHttpClient.sendRequest(new TriggerExtractionRequest({ planLuid: planLuid, selectedItems: selectedItems }))
    .then(function(result: IExecution) { return !result || hasErrors(result) ? Promise.reject(result) : result; });
}

export function getExecutionStatus(backgroundJobId: number): Promise<IExecution> {
  return WebHttpClient.sendRequest(new GetExecutionStatusRequest({ backgroundJobId: backgroundJobId }))
    .then(function(result: IExecution) { return !result || hasErrors(result) ? Promise.reject(result) : result; });
}

export interface IExecutionPoller {
  stop: () => void;
}

/**
 * Polls the execution's status every STATUS_POLL_INTERVAL_MS, reporting each update, until it reaches a
 * terminal state or is stopped. A few consecutive failed polls are tolerated (transient errors); after
 * MAX_STATUS_POLL_FAILURES, `onError` is called and polling stops. The extraction itself keeps running
 * server-side regardless.
 */
export function pollExecution(
  backgroundJobId: number,
  onUpdate: (execution: IExecution) => void,
  onError: (error: any) => void
): IExecutionPoller {
  let stopped = false;
  let failures = 0;
  let timer: number;

  const schedule = function(): void {
    timer = window.setTimeout(poll, STATUS_POLL_INTERVAL_MS);
  };
  const poll = function(): void {
    getExecutionStatus(backgroundJobId).then(
      function(execution) {
        if (stopped) {
          return;
        }
        failures = 0;
        onUpdate(execution);
        if (!isTerminalState(execution.state)) {
          schedule();
        }
      },
      function(error) {
        if (stopped) {
          return;
        }
        failures++;
        Tableau.Logger().info('Content Relay: getExecutionStatus failed (' + failures + '): ' + error);
        if (failures >= MAX_STATUS_POLL_FAILURES) {
          onError(error);
        } else {
          schedule();
        }
      }
    );
  };

  schedule();
  return {
    stop: function() {
      stopped = true;
      window.clearTimeout(timer);
    },
  };
}
